import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { AxiosError, AxiosInstance } from 'axios';
import { t } from 'i18next';
import { yupResolver } from '@hookform/resolvers/yup';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';

import { MainCustomButton } from '../../../components/Buttons';
import { StepperItem } from '../../../components/Stepper';
import { CustomTooltip, Input, PageHeadline } from '../../../components/atoms';
import { CustomInput } from '../../../components/inputs';
import { FormPhoneInput } from '../../../components/Form/FormPhoneInput';

import { useApi } from '../../../services/hooks/apiHook';
import { CompanyRegisteringService } from '../../../services/CompanyRegisteringService';
import { ICurrency, IRelevantActivity } from '../../../services/Interfaces/ICompanyRegisteringService';

import { useAuth } from '../../../store/auth';
import { Steps, useRegisteringStore } from './store/registering.store';
import { errorMessageAdapter } from '../../../adapters';
import { errorToast } from '../../../utils/toasts';
import { useAppSelector } from '../../../hooks/hooksRedux';
import { RootState } from '../../../store';
import { defaultColors } from '../../../styles/style-guide';
import { CustomInputNumberFormat } from '../../../components/inputs/CustomInputNumberFormat/CustomInputNumberFormat';
import { formatThousands } from '../../../utils/numbers';

export type FormDataTypeRegisterCompany = {
  IBCPrimaryName: string;
  IBCSecondName: string;
  IBCThirdName: string;
  activityAddress: string;
  commercialAddress: string;
  relevantActivity: string;
  totalAuthorizedShares: number;
  parValue: string;
  totalIssuedShares: string;
  currency: string;
  phone: string;
  ddi: string;
  primaryEmail: string;
};

const getUSDCurrencySlug = (items: ICurrency[] = []): string => {
  const currency = items.find((item) => item.acronym === 'USD');

  return String(currency?.id || '');
};

const DashboardRegisteringCompanyPage: React.FunctionComponent = () => {
  const [loading, setLoading] = useState(false);
  const [currencies, setCurrencies] = useState<ICurrency[]>([]);
  const [relevantAcitvities, setRelevantActivites] = useState<IRelevantActivity[]>([]);
  const [totalAuthorizedSharesControled, setTotalAuthorizedSharesControled] = useState<number>(50000);

  const { colors } = useAppSelector((state: RootState) => state.styleGuide.data);

  const primaryColor = colors.primary || defaultColors.primary;

  const { token, selectCompany, configSlug, user } = useAuth();
  const { i18n } = useTranslation();
  const { setStep } = useRegisteringStore();

  const api: AxiosInstance = useApi();

  const lastRelevantActivitiesFetchKeyRef = useRef<string | null>(null);
  const initialLanguageSyncRef = useRef(true);

  // Memoize headers to prevent unnecessary re-renders
  const headers = useMemo(
    () => ({
      Authorization: `Bearer ${token}`,
      'Content-Language': i18n.language.toLowerCase()
    }),
    [token, i18n.language]
  );

  useEffect(() => {
    (async () => {
      const data = await CompanyRegisteringService.listCurrencies(api, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Language': i18n.language.toLowerCase() }
      });

      setCurrencies(data.data);
    })();
  }, [token, api, i18n.language]);

  // Fetch relevant activities with debounce on language change
  const fetchRelevantActivities = useCallback(async () => {
    if (!token) {
      return;
    }

    const fetchKey = `${token}|${i18n.language}`;
    if (lastRelevantActivitiesFetchKeyRef.current === fetchKey) {
      return;
    }

    lastRelevantActivitiesFetchKeyRef.current = fetchKey;

    try {
      const data = await CompanyRegisteringService.listRelevantActivities(api, {
        headers
      });

      setRelevantActivites(data.data);
    } catch (error) {
      console.error('Error fetching relevant activities:', error);
    }
  }, [token, api, headers, i18n.language]);

  useEffect(() => {
    if (!token) {
      return;
    }

    if (initialLanguageSyncRef.current) {
      initialLanguageSyncRef.current = false;
      void fetchRelevantActivities();
      return;
    }

    const timeout = setTimeout(() => {
      void fetchRelevantActivities();
    }, 700);

    return () => {
      clearTimeout(timeout);
    };
  }, [token, i18n.language, fetchRelevantActivities]);

  const isDelaware = user?.jurisdiction?.description === 'Delaware';

  const validationSchema = useMemo(
    () =>
      Yup.object().shape({
        IBCPrimaryName: Yup.string()
          .required(t('Required') || '')
          .test('mesma-string', t('others.othThreeNamesMustDiffer') || '', function () {
            const { IBCPrimaryName, IBCSecondName, IBCThirdName } = this.parent;
            return IBCPrimaryName !== IBCSecondName && IBCPrimaryName !== IBCThirdName;
          }),
        IBCSecondName: Yup.string()
          .required(t('Required') || '')
          .test('mesma-string', t('others.othThreeNamesMustDiffer') || '', function () {
            const { IBCPrimaryName, IBCSecondName, IBCThirdName } = this.parent;
            return IBCPrimaryName !== IBCSecondName && IBCPrimaryName !== IBCThirdName;
          }),
        IBCThirdName: Yup.string()
          .required(t('Required') || '')
          .test('mesma-string', t('others.othThreeNamesMustDiffer') || '', function () {
            const { IBCPrimaryName, IBCSecondName, IBCThirdName } = this.parent;
            return IBCPrimaryName !== IBCSecondName && IBCPrimaryName !== IBCThirdName;
          }),
        activityAddress: Yup.string().required(t('Required') || ''),
        commercialAddress: Yup.string().when([], (values, schema) => {
          return isDelaware ? schema.required(t('Required') || '') : schema.nullable();
        }),
        relevantActivity: Yup.string().required(t('Required') || ''),
        totalAuthorizedShares: Yup.number()
          .transform((value) => (isNaN(value) ? undefined : value))
          .required(t('Required') || '')
          .min(1, t('others.othMinValue') || '')
          .max(50000, `${t('others.othExceededTheShareLimit')}: 50.000`),
        totalIssuedShares: Yup.number()
          .transform((value) => (isNaN(value) ? undefined : value))
          .required(t('Required') || '')
          .min(1, t('others.othMinValue') || '')
          .max(
            totalAuthorizedSharesControled,
            `${t('others.othExceededTheShareLimit')}: ${formatThousands(totalAuthorizedSharesControled)}`
          ),
        currency: Yup.string().required(t('Required') || ''),
        phone: Yup.string().required(t('Required') || ''),
        ddi: Yup.string().required(t('Required') || ''),
        primaryEmail: Yup.string()
          .required(t('Required') || '')
          .email(t('OnboardingCreateAccountErrorEmailMustBe') || '')
      }),
    [i18n.language, isDelaware, totalAuthorizedSharesControled]
  );

  const {
    register,
    formState: { errors },
    handleSubmit,
    setValue,
    watch,
    control,
    trigger
  } = useForm<FormDataTypeRegisterCompany>({
    resolver: yupResolver(validationSchema, { context: { isDelaware } }),
    defaultValues: { currency: '', relevantActivity: '', totalAuthorizedShares: 0 },
    mode: 'onSubmit'
  });

  const totalAuthorizedShares = watch('totalAuthorizedShares');

  // Re-validate on language change only after errors already exist (never on mount)
  const prevLanguageRef = useRef(i18n.language);
  useEffect(() => {
    if (prevLanguageRef.current === i18n.language) return;
    prevLanguageRef.current = i18n.language;
    if (Object.keys(errors).length > 0) {
      void trigger();
    }
  }, [i18n.language, trigger, errors]);

  const validateTheNextStep = () => {
    if (!user?.jurisdiction) {
      console.error('Jurisdiction data not loaded');
      return;
    }

    const typeJurisdiction = user.jurisdiction.description;
    if (typeJurisdiction === 'Delaware') {
      setStep(Steps.Members);
    } else {
      setStep(Steps.Directors);
    }
  };

  const handleSubmitForm: SubmitHandler<FormDataTypeRegisterCompany> = async (data) => {
    try {
      setLoading(true);
      const payload = { ...data, configSlug };
      const response = await CompanyRegisteringService.registerCompany(api, payload, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Language': i18n.language.toLowerCase() }
      });
      setLoading(false);
      selectCompany(response.data);
      validateTheNextStep();
    } catch (e: any) {
      setLoading(false);
      errorToast(errorMessageAdapter(e as AxiosError));
    }
  };

  useEffect(() => {
    setValue('currency', getUSDCurrencySlug(currencies));
  }, [currencies]);

  // useEffect(() => {
  //   setTotalAuthorizedSharesControled(Number(totalAuthorizedShares));
  // }, [totalAuthorizedShares]);

  useEffect(() => {
    if (!totalAuthorizedShares) {
      setTotalAuthorizedSharesControled(0);
      return;
    }

    // Substitui vírgulas por ponto e remove qualquer caractere que não seja número ou ponto
    const normalized = totalAuthorizedShares.toString().replace(',', '.').replace(/[^\d.]/g, '');
    const parsedValue = parseFloat(normalized);

    setTotalAuthorizedSharesControled(!isNaN(parsedValue) ? parsedValue : 0);
  }, [totalAuthorizedShares]);

  return (
    <div className="flex flex-col gap-6 pb-8">
      <PageHeadline title={t('others.othRegistering')} />

      <div className="flex w-full flex-wrap items-center justify-between gap-4">
        <StepperItem text={t('others.othCompany')} step={1} active />
        {!isDelaware && (
          <>
            <StepperItem text={t('others.othDirectors')} step={2} />
            <StepperItem text={t('others.othShareholders')} step={3} />
          </>
        )}
        {isDelaware && <StepperItem text={t('descriptions.Members')} step={2} />}
        <StepperItem text={t('others.othBusiness')} step={!isDelaware ? 4 : 3} />
      </div>

      <form onSubmit={handleSubmit(handleSubmitForm)} className="mt-2">
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2 lg:gap-8">
          {/* Card: New Company Information */}
          <div className="overflow-hidden rounded-xl border border-[#E1E9EF] bg-white shadow-sm">
            <div className="border-b border-[#E1E9EF] bg-[#F8FAFC] px-5 py-4">
              <h2 className="text-base font-semibold text-gray-800">
                {t('others.othInformationYourNewCompany')}
              </h2>
              <p className="mt-0.5 text-xs font-medium uppercase tracking-wide text-gray-500">
                {t('others.othCompany')}
              </p>
            </div>
            <div className="space-y-5 p-5">
              <div className="grid grid-cols-1 gap-5 sm:grid-cols-3">
                <div className="sm:col-span-1">
                  <CustomInput
                    label={t('others.othIBCPrimaryName')}
                    tooltip={t('others.othIBCPrimaryNameTooltip') || ''}
                    id="IBCPrimaryName"
                    register={register}
                    error={errors.IBCPrimaryName?.message}
                  />
                </div>
                <div className="sm:col-span-1">
                  <CustomInput
                    label={t('others.othIBCSecondName')}
                    id="IBCSecondName"
                    register={register}
                    error={errors.IBCSecondName?.message}
                  />
                </div>
                <div className="sm:col-span-1">
                  <CustomInput
                    label={t('others.othIBCThirdName')}
                    id="IBCThirdName"
                    register={register}
                    error={errors.IBCThirdName?.message}
                  />
                </div>
              </div>

              <CustomInput
                label={`${isDelaware ? t('others.othManagerAddress') : t('others.othActivityAddress')}`}
                id="activityAddress"
                register={register}
                error={errors.activityAddress?.message}
              />
              {isDelaware && (
                <CustomInput
                  label={t('others.othMainCommercialAddress')}
                  id="commercialAddress"
                  register={register}
                  error={errors.commercialAddress?.message}
                />
              )}

              <FormPhoneInput
                label={t('others.othPrimaryPhone')}
                id="phone"
                name="phone"
                error={errors.phone?.message}
                onPhoneChange={([ddi, phone]) => {
                  setValue('ddi', ddi);
                  setValue('phone', phone);
                }}
                control={control}
              />

              <CustomInput
                label={t('others.othPrimaryEmail')}
                id="primaryEmail"
                register={register}
                error={errors.primaryEmail?.message}
              />

              <div className="space-y-1.5">
                <div className="flex items-center gap-2">
                  <label className="text-sm font-medium" style={{ color: primaryColor }}>
                    {t('others.othChooseRelevantActivity')}
                  </label>
                  <CustomTooltip content={t('others.othChooseRelevantActivityTooltip') || ''} />
                </div>
                <select
                  {...register('relevantActivity')}
                  className={`h-10 w-full rounded-lg border px-3 text-sm outline-none transition-colors focus:ring-2 focus:ring-blue-500/20 ${
                    errors.relevantActivity?.message
                      ? 'border-red-400 bg-red-50/50'
                      : 'border-[#CED4DA] bg-white focus:border-blue-600'
                  }`}
                >
                  <option value="" defaultChecked disabled>
                    {t('others.othSelectRelevantActivity')}
                  </option>
                  {relevantAcitvities.map((temp) => (
                    <option key={temp.description} value={temp.id}>
                      {temp.description}
                    </option>
                  ))}
                </select>
                {errors.relevantActivity?.message && (
                  <span className="text-xs text-red-500">{errors.relevantActivity.message}</span>
                )}
              </div>
            </div>
          </div>

          {/* Card: Share Capital */}
          <div className="overflow-hidden rounded-xl border border-[#E1E9EF] bg-white shadow-sm">
            <div className="border-b border-[#E1E9EF] bg-[#F8FAFC] px-5 py-4">
              <h2 className="text-base font-semibold text-gray-800">{t('others.othShareCapital')}</h2>
            </div>
            <div className="space-y-5 p-5">
              <CustomInputNumberFormat
                id="totalAuthorizedShares"
                label={`${isDelaware ? t('others.othTotalAuthorizedUnits') : t('others.othTotalAuthorizedShares')}`}
                tooltip={
                  (isDelaware
                    ? t('others.othNumberOfUnitsTooltip')
                    : t('others.othTotalAuthorizedSharesTooltip')) || ''
                }
                control={control}
                error={errors.totalAuthorizedShares?.message}
              />
              <Input
                label={t('others.othParValue') || ''}
                tooltip={t('others.othParValueTooltip') || ''}
                value={totalAuthorizedShares ? `US$ ${totalAuthorizedShares.toLocaleString('pt-BR')}` : ''}
                disabled
              />
              <CustomInputNumberFormat
                id="totalIssuedShares"
                label={
                  isDelaware
                    ? t('others.othTotalIssuedUnits')
                    : t('others.othTotalIssuedShares')
                }
                tooltip={
                  (isDelaware
                    ? t('others.othNumberOfUnitsTooltip')
                    : t('others.othTotalIssuedSharesTooltip')) || ''
                }
                control={control}
                error={errors.totalIssuedShares?.message}
              />
              <div className="space-y-1.5">
                <label className="text-sm font-medium" style={{ color: primaryColor }}>
                  {t('others.othCurrency')}
                </label>
                <select
                  {...register('currency')}
                  className={`h-10 w-full rounded-lg border px-3 text-sm outline-none transition-colors focus:ring-2 focus:ring-blue-500/20 ${
                    errors.currency?.message
                      ? 'border-red-400 bg-red-50/50'
                      : 'border-[#CED4DA] bg-white focus:border-blue-600'
                  }`}
                >
                  <option value="" disabled>
                    {t('others.othSelectCurrency')}
                  </option>
                  {currencies
                    .filter((currency) => currency.acronym === 'USD')
                    .map((currency) => (
                      <option key={currency.id} value={currency.id}>
                        {currency.acronym}
                      </option>
                    ))}
                </select>
                {errors.currency?.message && (
                  <span className="text-xs text-red-500">{errors.currency.message}</span>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="mt-8 flex justify-end border-t border-gray-100 pt-6">
          <MainCustomButton text={t('buttons.btnNext')} type="submit" loading={loading} />
        </div>
      </form>
    </div>
  );
};

export { DashboardRegisteringCompanyPage };
